import React from 'react';
import { Icons } from '../../Icon.jsx';
import { calculateRect, measureNode } from '../../../utils.js';
import { SnapManager } from '../../../services/SnapManager.js';

const RectTransform = ({ primaryNode, parentSize, onChange, selectedNodes, renderField, settings, activeNodes, containerNode, canvasRef, canvasScale, activeBreakpoint = 'default', visualBreakpoint }) => {
    const n = primaryNode;
    const a = n.anchors;

    // Detect Current Modes
    const getAlignX = () => {
        if (a.minX === 0 && a.maxX === 1) return 'Stretch';
        if (a.minX === 0.5 && a.maxX === 0.5) return 'Center';
        if (a.minX === 1 && a.maxX === 1) return 'Right';
        return 'Left'; // Default to Left for 0-0 or custom
    };

    const getAlignY = () => {
        if (a.minY === 0 && a.maxY === 1) return 'Stretch';
        if (a.minY === 0.5 && a.maxY === 0.5) return 'Middle';
        if (a.minY === 1 && a.maxY === 1) return 'Bottom';
        return 'Top';
    };

    const alignX = getAlignX();
    const alignY = getAlignY();

    // -- Handlers --
    const update = (updates, skipSnap = false) => {
        // Apply snapping if updating x or y position
        if (!skipSnap && settings && activeNodes && (updates.x !== undefined || updates.y !== undefined)) {
            const currentRect = calculateRect(n, parentSize.width, parentSize.height);
            const targetX = updates.x !== undefined ? updates.x : n.x;
            const targetY = updates.y !== undefined ? updates.y : n.y;

            // Convert from offset to absolute position for snapping
            const anchorX = a.minX * parentSize.width;
            const anchorY = a.minY * parentSize.height;
            const absoluteX = anchorX + targetX;
            const absoluteY = anchorY + targetY;

            const snapResult = SnapManager.applySnapToPosition(
                absoluteX,
                absoluteY,
                currentRect.width,
                currentRect.height,
                settings,
                selectedNodes.map(x => x.id),
                activeNodes,
                parentSize.width,
                parentSize.height,
                containerNode,
                null,
                visualBreakpoint || activeBreakpoint
            );

            // Convert back to offset
            const snappedOffsetX = snapResult.x - anchorX;
            const snappedOffsetY = snapResult.y - anchorY;

            updates = {
                ...updates,
                ...(updates.x !== undefined ? { x: snappedOffsetX } : {}),
                ...(updates.y !== undefined ? { y: snappedOffsetY } : {})
            };
        }

        onChange(updates, selectedNodes.map(x => x.id));
    };

    const setAlignX = (mode) => {
        let newAnchors = { ...a };
        // 1. Determine new Anchors (Preserve Y)
        switch (mode) {
            case 'Left': newAnchors.minX=0; newAnchors.maxX=0; break;
            case 'Center': newAnchors.minX=0.5; newAnchors.maxX=0.5; break;
            case 'Right': newAnchors.minX=1; newAnchors.maxX=1; break;
            case 'Stretch': newAnchors.minX=0; newAnchors.maxX=1; break;
        }

        let newX = n.x;
        let newWidth = n.width;

        if (mode === 'Stretch') {
             // Match Parent Width (Snap)
             newX = 0;
             newWidth = 0;
        } else {
             // Alignments - preserve current visual size, snap position to anchor
             const currentRect = calculateRect(n, parentSize.width, parentSize.height);
             const w = currentRect.width;
             newWidth = w;

             if (mode === 'Left') newX = 0;
             if (mode === 'Center') newX = -w / 2;
             if (mode === 'Right') newX = -w;
        }

        update({ anchors: newAnchors, x: newX, width: newWidth }, true);
    };

    const setAlignY = (mode) => {
        let newAnchors = { ...a };
        // 1. Determine new Anchors (Preserve X)
        switch (mode) {
            case 'Top': newAnchors.minY=0; newAnchors.maxY=0; break;
            case 'Middle': newAnchors.minY=0.5; newAnchors.maxY=0.5; break;
            case 'Bottom': newAnchors.minY=1; newAnchors.maxY=1; break;
            case 'Stretch': newAnchors.minY=0; newAnchors.maxY=1; break;
        }

        let newY = n.y;
        let newHeight = n.height;

        if (mode === 'Stretch') {
             // Match Parent Height (Snap)
             newY = 0;
             newHeight = 0;
        } else {
             // Alignments - preserve current visual size, snap position to anchor
             const currentRect = calculateRect(n, parentSize.width, parentSize.height);
             const h = currentRect.height;
             newHeight = h;

             if (mode === 'Top') newY = 0;
             if (mode === 'Middle') newY = -h / 2;
             if (mode === 'Bottom') newY = -h;
        }

        update({ anchors: newAnchors, y: newY, height: newHeight }, true);
    };

    // Calculate derived display values
    const rightVal = -n.width - n.x; // right margin
    const bottomVal = -n.height - n.y; // bottom margin

    return (
        <div className="space-y-4">

            {/* HORIZONTAL SECTION */}
            <div className="space-y-2">
                <div className="flex items-center justify-between text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
                    <span>Horizontal</span>
                    <span className="text-blue-600 bg-blue-50 px-1 rounded">{alignX}</span>
                </div>

                {/* Mode Buttons */}
                <div className="flex bg-slate-100 p-0.5 rounded border border-slate-200">
                    {[
                        { mode: 'Left', icon: Icons.AlignLeft },
                        { mode: 'Center', icon: Icons.AlignCenter },
                        { mode: 'Right', icon: Icons.AlignRight },
                        { mode: 'Stretch', icon: Icons.HorizontalLayout }
                    ].map(btn => (
                        <button
                            key={btn.mode}
                            onClick={() => setAlignX(btn.mode)}
                            className={`flex-1 flex items-center justify-center py-1 rounded hover:bg-white hover:shadow-sm transition-all ${alignX === btn.mode ? 'bg-white shadow text-blue-600' : 'text-gray-400'}`}
                            title={btn.mode}
                        >
                            <btn.icon className="w-3.5 h-3.5" />
                        </button>
                    ))}
                </div>

                {/* Horizontal Inputs */}
                <div className="grid grid-cols-2 gap-2">
                    {alignX === 'Stretch' ? (
                        <>
                            {renderField('Margin Left', n.x, (v) => update({ x: v }), 'number', undefined, 'x')}
                            {renderField('Margin Right', rightVal, (v) => update({ width: -n.x - v }), 'number', undefined, 'width')}
                        </>
                    ) : (
                        <>
                            {renderField(alignX === 'Center' ? 'Pos X' : 'X Offset', n.x, (v) => update({ x: v }), 'number', undefined, 'x')}
                            {renderField('Width', n.width, (v) => update({ width: v }), 'number', undefined, 'width')}
                        </>
                    )}
                </div>
            </div>

            <div className="h-px bg-slate-100 my-2" />

            {/* VERTICAL SECTION */}
            <div className="space-y-2">
                <div className="flex items-center justify-between text-[10px] text-gray-500 uppercase tracking-wider font-semibold">
                    <span>Vertical</span>
                    <span className="text-blue-600 bg-blue-50 px-1 rounded">{alignY}</span>
                </div>

                 {/* Mode Buttons */}
                 <div className="flex bg-slate-100 p-0.5 rounded border border-slate-200">
                    {[
                        { mode: 'Top', icon: Icons.AlignTop },
                        { mode: 'Middle', icon: Icons.AlignMiddle },
                        { mode: 'Bottom', icon: Icons.AlignBottom },
                        { mode: 'Stretch', icon: Icons.VerticalLayout }
                    ].map(btn => (
                        <button
                            key={btn.mode}
                            onClick={() => setAlignY(btn.mode)}
                            className={`flex-1 flex items-center justify-center py-1 rounded hover:bg-white hover:shadow-sm transition-all ${alignY === btn.mode ? 'bg-white shadow text-blue-600' : 'text-gray-400'}`}
                            title={btn.mode}
                        >
                            <btn.icon className="w-3.5 h-3.5" />
                        </button>
                    ))}
                </div>

                {/* Vertical Inputs */}
                <div className="grid grid-cols-2 gap-2">
                    {alignY === 'Stretch' ? (
                        <>
                            {renderField('Margin Top', n.y, (v) => update({ y: v }), 'number', undefined, 'y')}
                            {renderField('Margin Bottom', bottomVal, (v) => update({ height: -n.y - v }), 'number', undefined, 'height')}
                        </>
                    ) : (
                        <>
                            {renderField(alignY === 'Middle' ? 'Pos Y' : 'Y Offset', n.y, (v) => update({ y: v }), 'number', undefined, 'y')}
                            {renderField('Height', n.height, (v) => update({ height: v }), 'number', undefined, 'height')}
                        </>
                    )}
                </div>
            </div>

            {/* Raw Anchors Foldout (Advanced) */}
            <details className="pt-2">
                <summary className="text-[9px] text-gray-400 cursor-pointer hover:text-blue-500 mb-2">Advanced Anchors</summary>
                <div className="grid grid-cols-4 gap-1 p-2 bg-slate-50 rounded border border-slate-100">
                    <div className="col-span-4 text-[9px] text-gray-400 text-center mb-1">Normalized Coordinates (0.0 - 1.0)</div>
                    <div className="col-span-2 space-y-1">
                        <div className="flex gap-1 items-center">
                            <span className="text-[9px] w-4 text-right">Min</span>
                            <input className="w-full text-[10px] border rounded px-1 py-0.5" type="number" step="0.1" value={a.minX} onChange={e=>update({anchors: {...a, minX: parseFloat(e.target.value)}})} placeholder="X"/>
                            <input className="w-full text-[10px] border rounded px-1 py-0.5" type="number" step="0.1" value={a.minY} onChange={e=>update({anchors: {...a, minY: parseFloat(e.target.value)}})} placeholder="Y"/>
                        </div>
                    </div>
                    <div className="col-span-2 space-y-1">
                         <div className="flex gap-1 items-center">
                            <span className="text-[9px] w-4 text-right">Max</span>
                            <input className="w-full text-[10px] border rounded px-1 py-0.5" type="number" step="0.1" value={a.maxX} onChange={e=>update({anchors: {...a, maxX: parseFloat(e.target.value)}})} placeholder="X"/>
                            <input className="w-full text-[10px] border rounded px-1 py-0.5" type="number" step="0.1" value={a.maxY} onChange={e=>update({anchors: {...a, maxY: parseFloat(e.target.value)}})} placeholder="Y"/>
                        </div>
                    </div>
                </div>
            </details>
        </div>
    );
};

export default RectTransform;
